import api from '@api/instance';
import { Page } from '@models/page';

import {
  SalesforceConnection,
  SalesforceTestConnection,
  SalesforceConnectionType,
} from '@modules/salesforce/models';
import { useInfiniteQuery } from 'react-query';
import { getNextPageParam } from '@hooks/use-react-query-caching';

const nameSpace = '/salesforce_connections';
const queryKey = 'salesforce-connections-infinite-query';

const show = (connectionId: number) => {
  return api
    .get(nameSpace + '/' + connectionId)
    .then((res) => new SalesforceConnection().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const create = (connection: SalesforceConnection) => {
  return api
    .post(nameSpace, connection)
    .then((res) => new SalesforceConnection().deserialize(res))
    .catch((e) => {
      return Promise.reject(e.response?.data || e);
    });
};

const update = (payload: SalesforceConnection) => {
  return api
    .put<SalesforceConnection>(nameSpace + `/${payload.id}`, payload)
    .then((res) => new SalesforceConnection().deserialize(res))
    .catch((e) => {
      return Promise.reject(e.response?.data || e);
    });
};

const syncMetadata = (conectionId: number) => {
  return api
    .get(nameSpace + '/' + conectionId + '/sync_metadata')
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const findAll = (query?, size?, page?, sort?) => {
  return api
    .get(nameSpace, {
      query,
      size,
      page,
      sort,
    })
    .then((res) =>
      new Page<SalesforceConnection>().deserialize(res, SalesforceConnection),
    )
    .catch((e) => Promise.reject(e));
};

const findbyApplicationid = (query?, sort?, size?, page?) => {
  return api
    .get(nameSpace + '/connections', { query, sort, size, page })
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

export const getAllGlobalConnections = ({
  query,
  sort,
  size,
  callback,
  enabled,
}: {
  query: string;
  sort?: string;
  size?: number;
  callback?: Function;
  enabled?: boolean;
}) =>
  useInfiniteQuery({
    queryKey: `${queryKey}-${query}`,
    queryFn: async ({ pageParam = 0 }) => {
      try {
        const response = await findbyApplicationid(
          query,
          sort,
          size,
          pageParam,
        );
        callback?.(response);
        return response;
      } catch (e) {
        throw e;
      }
    },
    getNextPageParam,
    cacheTime: 0,
    enabled: !!query && enabled,
  });

const getVersionSpecificConnection = (
  applicationVersionId: any,
  applicationId: any,
  query?,
) => {
  return api
    .get(
      nameSpace +
        '/' +
        applicationId +
        '/connections' +
        '/' +
        applicationVersionId,
      { query },
    )
    .catch((e) => Promise.reject(e));
};

const updateVersionId = ({
  applicationVersionId,
  applicationId,
  connection,
}) => {
  return api
    .post(
      `${nameSpace}/${applicationVersionId}/update_version_mapping/${applicationId}`,
      connection,
    )
    .then((res) => new SalesforceConnection().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const testSFConnection = (connection: SalesforceConnection) => {
  return api
    .post(`${nameSpace}/test-connection`, connection)
    .then((res) => {
      console.log('API Response:', res); // Check what the response actually contains
      return new SalesforceConnection().deserialize(res);
    })
    .catch((e) => {
      console.error('Error in testSFConnection:', e);
      return Promise.reject(e.response?.data || e);
    });
};

const remove = (conectionId: number) => {
  return api
    .remove(`${nameSpace}/${conectionId}`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const findMetadataConnectionByVersionIdAndConnectionType = (
  versionId: number,
  connectionType: SalesforceConnectionType,
) => {
  return api
    .get<SalesforceConnection>(
      `${nameSpace}/${versionId}/salesforce_connection_version_mapping/${connectionType}`,
    )
    .then((res) => new SalesforceConnection().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const SalesforceConnectionService = {
  show,
  create,
  syncMetadata,
  findAll,
  update,
  remove,
  findbyApplicationid,
  updateVersionId,
  getVersionSpecificConnection,
  getAllGlobalConnections,
  testSFConnection,
  findMetadataConnectionByVersionIdAndConnectionType,
};

export default SalesforceConnectionService;
